import { Command } from 'commander';
import { confirm } from '@inquirer/prompts';
import chalk from 'chalk';
import { ConfigService } from '../services/config.service.js';
import { ComponentsService } from '../services/components.service.js';
import { ValidationError } from '../errors/index.js';
import { trackAction } from '../services/tracking.service.js';

/**
 * Create update command
 */
export function createUpdateCommand(): Command {
    return new Command('update')
        .description('Update installed components to latest versions')
        .option('--force', 'Force update even if files are modified locally')
        .action(async (options: { force?: boolean }) => {
            await handleUpdate(options);
        });
}

async function handleUpdate(options: { force?: boolean }) {
    const configService = new ConfigService();
    const config = await configService.load();
    if (!config) {
        throw new ValidationError('Not initialized. Run "jai1 auth" first.');
    }

    const componentsService = new ComponentsService();
    const installed = await componentsService.getInstalled();
    const filepaths = Object.keys(installed);

    if (filepaths.length === 0) {
        console.log(chalk.yellow('📦 Chưa có components nào được cài đặt.'));
        console.log(chalk.dim('   Chạy "jai1 apply" để thêm components.'));
        return;
    }

    console.log(chalk.cyan('🔍 Đang kiểm tra cập nhật...'));

    try {
        const checksums = await componentsService.getChecksums(config, filepaths);
        const updates: string[] = [];
        const upToDate: string[] = [];
        const deprecated: string[] = [];

        for (const fp of filepaths) {
            const current = installed[fp];
            const latest = checksums[fp];

            if (!latest) {
                deprecated.push(fp);
                continue;
            }

            if (current.checksum !== latest.checksum) {
                updates.push(fp);
            } else {
                upToDate.push(fp);
            }
        }

        // Show deprecated components
        if (deprecated.length > 0) {
            console.log(chalk.yellow('\n⚠️  Components không còn trên server (deprecated):'));
            for (const fp of deprecated) {
                console.log(chalk.dim(`   - ${fp}`));
            }
        }

        if (updates.length === 0) {
            console.log(chalk.green('\n✅ Tất cả components đã là phiên bản mới nhất.'));
            if (upToDate.length > 0) {
                console.log(chalk.dim(`   ${upToDate.length} component(s) đã cập nhật.`));
            }
            return;
        }

        console.log(chalk.cyan(`\n📦 Tìm thấy ${chalk.bold(updates.length)} cập nhật:`));
        for (const fp of updates) {
            const current = installed[fp];
            const latest = checksums[fp];
            console.log(
                `   ${chalk.white(fp)} ` +
                chalk.dim(`v${current.version}`) +
                chalk.cyan(' → ') +
                chalk.green(`v${latest.version}`)
            );
        }
        console.log();

        if (!options.force) {
            const shouldUpdate = await confirm({ message: 'Cập nhật ngay?', default: true });
            if (!shouldUpdate) {
                console.log(chalk.dim('Đã hủy cập nhật.'));
                return;
            }
        }

        // Perform updates
        const targetDir = process.cwd() + '/.jai1';
        let updatedCount = 0;
        const backupPaths: string[] = [];

        console.log();
        for (const fp of updates) {
            try {
                process.stdout.write(chalk.cyan(`📥 Đang cập nhật ${chalk.white(fp)}...`));
                const backupPath = await componentsService.backupFile(fp, targetDir);
                if (backupPath) {
                    backupPaths.push(backupPath);
                }

                await componentsService.install(config, fp, targetDir);
                console.log(chalk.green(' ✓'));
                updatedCount++;
            } catch (error) {
                console.log(chalk.red(' ✗'));
                console.log(chalk.red(`   Lỗi: ${error instanceof Error ? error.message : 'Unknown error'}`));
            }
        }

        console.log();
        if (updatedCount === updates.length) {
            console.log(chalk.green(`✅ Hoàn tất: ${updatedCount}/${updates.length} đã cập nhật.`));
        } else {
            console.log(chalk.yellow(`⚠️  Hoàn tất: ${updatedCount}/${updates.length} đã cập nhật.`));
        }

        if (backupPaths.length > 0) {
            console.log(chalk.dim(`\n📁 Đã tạo backup tại .jai1_backup/`));

            const cleanBackups = await confirm({ message: 'Xóa các backup này?', default: false });
            if (cleanBackups) {
                await componentsService.clearBackups(process.cwd());
                console.log(chalk.green('🗑️  Đã xóa backups.'));
            } else {
                console.log(chalk.dim('💡 Chạy "jai1 clean" để xóa sau.'));
            }
        }

        trackAction('components_update', { count: updatedCount });

    } catch (error) {
        throw new Error(`Kiểm tra cập nhật thất bại: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}
